// Phase RV-6a — FLEX Builder Panel.
//
// Source: ROLLOVER Intelligence Enhancement v1.0 §11.2 (FLEX Builder).
//
// Doctrine carry-forward:
//   - Compose I0 primitives only.
//   - N15 absolute: zero references to chain mutation paths.
//   - N19 always-visible disclaimer.
//   - "STRIKZA NEVER RECOMMENDS A STAKE" (JA.4 pattern, mirror).
//   - No primitive edits, no --sa-* token mutations.

import type { CSSProperties } from "react";
import { useState } from "react";
import { HairlineRow, Pill, Stat } from "../super-acca";
import type { FlexLeg } from "../../rollover/flexBuilderHelpers.ts";
import type { BoardCandidate } from "../../rollover/upcomingBoardHelpers.ts";
import type { RolloverTier } from "../../rollover/tierConfig.ts";
import { useFlexBuilder } from "../../rollover/useFlexBuilder.ts";

// N19 disclaimer (locked string; grepped by nRules.test.ts).
export const FLEX_DISCLAIMER =
  "FLEX is a personal side bet — does not affect your active chain";

// JA.4 honesty caption (grepped).
export const FLEX_NO_STAKE_RECOMMENDATION = "STRIKZA NEVER RECOMMENDS A STAKE";

// No-place disclaimer (mirror platform pattern).
export const FLEX_NO_PLACE_DISCLAIMER =
  "STRIKZA never places bets. Copy this slip to your bookmaker manually.";

const TIER_ACCENT: Record<RolloverTier, string> = {
  "2x": "var(--sa-accent-saffron)",
  "3x": "var(--sa-fg-muted)",
  "4x": "var(--sa-accent-saffron)",
  "5x": "var(--sa-accent-saffron)",
  "6x": "var(--sa-accent-saffron)",
};

const TIER_LABEL: Record<RolloverTier, string> = {
  "2x": "2×",
  "3x": "3×",
  "4x": "4×",
  "5x": "5×",
  "6x": "6×",
};

export interface FlexBuilderPanelProps {
  pool: readonly BoardCandidate[];
  windowLabel: BoardCandidate["window_label"] | null;
  windowPublicationAt: Date | null;
  isAuthed: boolean;
  now?: Date;
  style?: CSSProperties;
}

export function FlexBuilderPanel({
  pool,
  windowLabel,
  windowPublicationAt,
  isAuthed,
  now,
  style,
}: FlexBuilderPanelProps): JSX.Element {
  const builder = useFlexBuilder({ pool, windowLabel, windowPublicationAt, now });
  const [stakeRaw, setStakeRaw] = useState<string>("");
  const [success, setSuccess] = useState<string | null>(null);

  const stake = (() => {
    const n = Number(stakeRaw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const potential =
    stake !== null && builder.combinedOdds > 0
      ? Math.round(stake * builder.combinedOdds * 100) / 100
      : null;

  const lockDisabled =
    !isAuthed ||
    builder.locking ||
    builder.legs.length < 2 ||
    !builder.lockability.ok ||
    !builder.sameFixtureConflict.ok ||
    !builder.contradictions.ok;

  async function handleLock(): Promise<void> {
    setSuccess(null);
    const result = await builder.lockSlip(stake);
    if (result.ok) {
      setSuccess(result.flex_slip_id);
      setStakeRaw("");
    }
  }

  function handleClear(): void {
    if (builder.legs.length >= 3) {
      const ok = typeof window !== "undefined"
        ? window.confirm("Clear all FLEX legs?")
        : true;
      if (!ok) return;
    }
    builder.clearDraft();
    setSuccess(null);
  }

  return (
    <section
      data-testid="flex-builder-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        padding: 16,
        border: "1px solid var(--sa-hairline)",
        borderRadius: 4,
        background: "var(--sa-bg-elev-1, transparent)",
        ...style,
      }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontFamily: "var(--sa-font-stat)", fontSize: 16, letterSpacing: 1, textTransform: "uppercase", color: "var(--sa-fg-primary)" }}>
          FLEX Builder
        </div>
        <Pill borderColor="var(--sa-hairline)">{builder.legs.length} LEGS</Pill>
      </header>

      {/* N19 disclaimer — always visible */}
      <div
        data-testid="flex-disclaimer"
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          color: "var(--sa-fg-muted)",
          lineHeight: 1.4,
        }}
      >
        {FLEX_DISCLAIMER}
      </div>

      {builder.legs.length === 0 ? (
        <div
          data-testid="flex-empty"
          style={{
            padding: 16,
            border: "1px dashed var(--sa-hairline)",
            borderRadius: 4,
            color: "var(--sa-fg-muted)",
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            textAlign: "center",
          }}
        >
          Add eligible legs from the Upcoming Picks Board to build your FLEX slip.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {builder.legs.map((leg) => (
            <LegLine
              key={leg.candidate_id}
              leg={leg}
              onRemove={() => builder.removeLeg(leg.candidate_id)}
            />
          ))}
        </div>
      )}

      {/* Correlation warning */}
      {!builder.sameFixtureConflict.ok && (
        <div
          data-testid="flex-correlation-warning"
          style={{
            padding: 10,
            border: "1px solid var(--sa-fg-danger, #b00020)",
            borderRadius: 4,
            color: "var(--sa-fg-danger, #b00020)",
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
          }}
        >
          You already have {builder.sameFixtureConflict.conflict.a.match_name} in your FLEX slip. Only one leg per fixture.
        </div>
      )}

      {/* Contradiction flag */}
      {!builder.contradictions.ok && (
        <div
          data-testid="flex-contradiction-warning"
          style={{
            padding: 10,
            border: "1px solid var(--sa-accent-saffron)",
            borderRadius: 4,
            color: "var(--sa-accent-saffron)",
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
          }}
        >
          {builder.contradictions.conflicts.map((c, i) => (
            <div key={i}>
              {c.a.market ?? "?"} and {c.b.market ?? "?"} contradict each other.
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Stat
          label="COMBINED ODDS"
          value={builder.combinedOdds > 0 ? builder.combinedOdds.toFixed(2) : "—"}
          display
          accent
        />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div
            style={{
              fontFamily: "var(--sa-font-stat)",
              fontSize: 11,
              letterSpacing: "0.18em",
              color: "var(--sa-fg-muted)",
              textTransform: "uppercase",
            }}
          >
            STAKE (GH₵)
          </div>
          <input
            data-testid="flex-stake-input"
            type="number"
            inputMode="decimal"
            min={0}
            step={1}
            value={stakeRaw}
            onChange={(e) => setStakeRaw(e.target.value)}
            placeholder=""
            style={{
              width: "100%",
              marginTop: 6,
              padding: "8px 10px",
              border: "1px solid var(--sa-hairline)",
              borderRadius: 3,
              background: "transparent",
              color: "var(--sa-fg-primary)",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 14,
            }}
          />
          <div
            style={{
              marginTop: 4,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              letterSpacing: "0.14em",
              color: "var(--sa-fg-muted)",
              textTransform: "uppercase",
            }}
          >
            {FLEX_NO_STAKE_RECOMMENDATION}
          </div>
        </div>
        <Stat
          label="POTENTIAL RETURN"
          value={potential !== null ? `GH₵${potential.toFixed(2)}` : "—"}
          display
        />
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid="flex-lock-button"
          disabled={lockDisabled}
          onClick={() => void handleLock()}
          style={{
            background: lockDisabled ? "transparent" : "var(--sa-accent-saffron)",
            color: lockDisabled ? "var(--sa-fg-muted)" : "var(--sa-bg-primary, #000)",
            border: "1px solid var(--sa-accent-saffron)",
            padding: "10px 16px",
            borderRadius: 3,
            fontFamily: "var(--sa-font-stat)",
            fontSize: 13,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            cursor: lockDisabled ? "not-allowed" : "pointer",
          }}
        >
          {builder.locking
            ? "LOCKING…"
            : stake !== null
              ? `LOCK MY FLEX — GH₵${stake.toFixed(2)}`
              : "LOCK MY FLEX"}
        </button>
        <button
          type="button"
          data-testid="flex-clear-button"
          onClick={handleClear}
          disabled={builder.legs.length === 0}
          style={{
            background: "transparent",
            color: "var(--sa-fg-muted)",
            border: "none",
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            textDecoration: "underline",
            cursor: builder.legs.length === 0 ? "default" : "pointer",
          }}
        >
          Clear FLEX
        </button>
      </div>

      {!isAuthed && (
        <div
          data-testid="flex-signin-prompt"
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-fg-muted)",
          }}
        >
          Sign in to lock FLEX slips.
        </div>
      )}

      {builder.lockError && (
        <div
          data-testid="flex-lock-error"
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-fg-danger, #b00020)",
          }}
        >
          {builder.lockError}
        </div>
      )}

      {success !== null && (
        <div data-testid="flex-lock-success" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Pill color="var(--sa-accent-saffron)" borderColor="var(--sa-accent-saffron)">
            LOCKED
          </Pill>
          <span
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
            }}
          >
            {success.slice(0, 8)}…
          </span>
        </div>
      )}

      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 10,
          color: "var(--sa-fg-muted)",
          lineHeight: 1.4,
          borderTop: "1px solid var(--sa-hairline)",
          paddingTop: 10,
        }}
      >
        {FLEX_NO_PLACE_DISCLAIMER}
      </div>
    </section>
  );
}

function LegLine({
  leg,
  onRemove,
}: {
  leg: FlexLeg;
  onRemove: () => void;
}): JSX.Element {
  return (
    <div data-testid={`flex-leg-${leg.candidate_id}`}>
      <HairlineRow style={{ borderLeft: `3px solid ${TIER_ACCENT[leg.tier]}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <Pill borderColor={TIER_ACCENT[leg.tier]} color={TIER_ACCENT[leg.tier]}>
            {TIER_LABEL[leg.tier]}
          </Pill>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <div
              style={{
                fontFamily: "var(--sa-font-stat)",
                fontSize: 12,
                letterSpacing: "0.06em",
                color: "var(--sa-fg-primary)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {leg.match_name}
            </div>
            <div
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 10,
                color: "var(--sa-fg-muted)",
              }}
            >
              {leg.market ?? "—"} · {leg.pick ?? "—"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 13,
              color: "var(--sa-fg-primary)",
            }}
          >
            {leg.odds !== null ? leg.odds.toFixed(2) : "—"}
          </div>
          <button
            type="button"
            data-testid={`flex-remove-${leg.candidate_id}`}
            onClick={onRemove}
            aria-label="Remove leg"
            style={{
              background: "transparent",
              border: "1px solid var(--sa-hairline)",
              color: "var(--sa-fg-muted)",
              padding: "2px 8px",
              borderRadius: 3,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
      </div>
      </HairlineRow>
    </div>
  );
}
